import dayjs from 'dayjs';
import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  FilterOperator
} from '@progress/kendo-data-query';
import {
  CPS_ACTIONS_ALLOWED_FOR_DOCUMENT,
  CPS_FIELD_TYPE,
  CpsFieldForView,
  CpsFieldType,
  CpsFieldTypeToFilterType,
  CpsFilterRequest,
  PERMISSIONS_FIELD
} from '@cs-cms/types';
import { getGearDateTimeFilterFormat } from '@cs-cms/utils';
import { getDateTimeRangeFilters } from './cpsGridHelpers';

export interface GridFilterBuildResult {
  gridFilters: CpsFilterRequest[];
  actionFilters: number[];
}

const mapActionDisplayNameToFlag = (
  actionDisplayName: string,
  translate: (key: string) => string
): number => {
  for (const key in CPS_ACTIONS_ALLOWED_FOR_DOCUMENT) {
    const actionKey = key.charAt(0) + key.slice(1).toLowerCase();
    const translatedValue = translate(`grid.filter${actionKey}`);
    if (translatedValue === actionDisplayName) {
      return CPS_ACTIONS_ALLOWED_FOR_DOCUMENT[key as keyof typeof CPS_ACTIONS_ALLOWED_FOR_DOCUMENT];
    }
  }
  return 0;
};

const buildDateFilter = (
  newFiltersDescriptors: FilterDescriptor[],
  filteredFieldName: string,
  filteredOperator: FilterOperator,
  filteredFieldType: CpsFieldType
): CpsFilterRequest[] => {
  const dateValue = newFiltersDescriptors[0].value;
  if (!dateValue) {
    return [];
  }

  if (filteredOperator === FilterOperator.EqualTo) {
    return getDateTimeRangeFilters(new Date(dateValue), filteredFieldName);
  }

  const localDate = dayjs(dateValue);
  const startOrEndOfDay =
    filteredOperator === FilterOperator.LessThanOrEqual ||
    filteredOperator === FilterOperator.GreaterThan
      ? localDate.endOf('day')
      : localDate.startOf('day');

  return [
    {
      fieldName: filteredFieldName,
      operator: filteredOperator,
      values: [getGearDateTimeFilterFormat(startOrEndOfDay.toDate())],
      fieldType: CpsFieldTypeToFilterType(filteredFieldType)
    } as CpsFilterRequest
  ];
};

const buildStandardFilter = (
  newFiltersDescriptors: FilterDescriptor[],
  filteredFieldName: string,
  filteredOperator: FilterOperator,
  filteredFieldType: CpsFieldType
): CpsFilterRequest[] => {
  const filteredValues = newFiltersDescriptors
    .filter(i => i.value !== undefined && i.value !== null)
    .map(i => i.value?.toString());

  return [
    {
      fieldName: filteredFieldName,
      operator: filteredOperator,
      values: filteredValues,
      fieldType: filteredFieldType
    } as CpsFilterRequest
  ];
};

interface DescriptorFilterMapping {
  gridFilters: CpsFilterRequest[];
  actionFlags: number[];
  isActionFilter: boolean;
}

const mapCompositeDescriptorToFilters = (
  descriptor: CompositeFilterDescriptor,
  cols: CpsFieldForView[],
  translate: (key: string) => string
): DescriptorFilterMapping => {
  const newFiltersDescriptors = descriptor.filters as FilterDescriptor[];
  if (newFiltersDescriptors.length === 0) {
    return { gridFilters: [], actionFlags: [], isActionFilter: false };
  }

  const filteredFieldName = newFiltersDescriptors[0].field as string;
  const filteredOperator = newFiltersDescriptors[0].operator as FilterOperator;

  if (filteredFieldName === PERMISSIONS_FIELD) {
    const filteredValues = newFiltersDescriptors
      .filter(i => i.value !== undefined && i.value !== null)
      .map(i => i.value?.toString());

    const actionFlags = filteredValues
      .map(actionDisplayName => mapActionDisplayNameToFlag(actionDisplayName ?? '', translate))
      .filter(flag => flag !== 0);

    return { gridFilters: [], actionFlags, isActionFilter: true };
  }

  const filteredFieldType = cols.find(field => field.name === filteredFieldName)?.type || CPS_FIELD_TYPE.TEXT;

  if (filteredFieldType === CPS_FIELD_TYPE.DATETIME || filteredFieldType === CPS_FIELD_TYPE.DATE) {
    return {
      gridFilters: buildDateFilter(newFiltersDescriptors, filteredFieldName, filteredOperator, filteredFieldType),
      actionFlags: [],
      isActionFilter: false
    };
  }

  return {
    gridFilters: buildStandardFilter(
      newFiltersDescriptors,
      filteredFieldName,
      filteredOperator,
      filteredFieldType
    ),
    actionFlags: [],
    isActionFilter: false
  };
};

export const buildFiltersFromComposite = (
  composite: CompositeFilterDescriptor | undefined,
  cols: CpsFieldForView[] | undefined,
  translate: (key: string) => string
): GridFilterBuildResult => {
  if (!composite || !cols) {
    return { gridFilters: [], actionFilters: [] };
  }

  let actionFilters: number[] = [];
  const gridFilters = composite.filters
    .filter(descriptor => (descriptor as CompositeFilterDescriptor).filters.length > 0)
    .flatMap(descriptor => {
      const mapping = mapCompositeDescriptorToFilters(descriptor as CompositeFilterDescriptor, cols, translate);
      if (mapping.isActionFilter) {
        actionFilters = mapping.actionFlags;
      }
      return mapping.gridFilters;
    });

  return { gridFilters, actionFilters };
};
